import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { CalendarDays } from 'lucide-react';
import { addQuarters, defaultFilingQuarter, quarterOfDate, quarterRange, compareQuarter } from '@btw/shared';
import { useOverview } from '../api/hooks';
import { useI18n } from '../i18n';
import { readStorage, writeStorage } from '../lib/hooks';

interface QuarterValue {
  /** The quarter pages should show (explicit choice or the server's default). */
  quarter: string;
  defaultQuarter: string;
  isDefault: boolean;
  setQuarter: (q: string | null) => void;
  /** Server "today" when known (APP_TODAY in dev), else the browser date. */
  today: string;
}

const QuarterContext = createContext<QuarterValue | null>(null);

function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Default quarter = the next quarter to file, as decided by the server (GET /api/overview without a quarter),
 * not the calendar quarter (§4.3). A user's explicit choice is kept for the session.
 */
export function QuarterProvider({ children }: { children: ReactNode }) {
  const overview = useOverview(null);
  const [chosen, setChosen] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem('btw.quarter');
    } catch {
      return readStorage('btw.quarter.fallback');
    }
  });
  const today = overview.data?.today ?? localToday();
  const defaultQuarter = overview.data?.quarter ?? defaultFilingQuarter(today);
  const value = useMemo<QuarterValue>(
    () => ({
      quarter: chosen ?? defaultQuarter,
      defaultQuarter,
      isDefault: !chosen || chosen === defaultQuarter,
      today,
      setQuarter: (q) => {
        setChosen(q);
        try {
          if (q) sessionStorage.setItem('btw.quarter', q);
          else sessionStorage.removeItem('btw.quarter');
        } catch {
          writeStorage('btw.quarter.fallback', q);
        }
      },
    }),
    [chosen, defaultQuarter, today],
  );
  return <QuarterContext.Provider value={value}>{children}</QuarterContext.Provider>;
}

export function useQuarter(): QuarterValue {
  const v = useContext(QuarterContext);
  if (!v) throw new Error('useQuarter outside QuarterProvider');
  return v;
}

/** Selectable quarters: the current calendar quarter back two years. */
export function quarterOptions(today: string, count = 8): string[] {
  const current = quarterOfDate(today);
  return Array.from({ length: count }, (_, i) => addQuarters(current, -i));
}

/** Quarter selector, top-right of pages, with the date range below (§4.3). */
export function QuarterSelect({ value, onChange }: { value?: string; onChange?: (q: string) => void }) {
  const { t, fmt } = useI18n();
  const ctx = useQuarter();
  const q = value ?? ctx.quarter;
  const opts = quarterOptions(ctx.today);
  if (!opts.includes(q)) opts.push(q);
  opts.sort((a, b) => compareQuarter(b, a));
  const range = quarterRange(q);
  return (
    <div className="quarter-select">
      <div className="quarter-select__control">
        <CalendarDays size={18} className="cal" aria-hidden="true" />
        <select
          className="select"
          aria-label={t('quarter.label')}
          value={q}
          onChange={(e) => (onChange ? onChange(e.target.value) : ctx.setQuarter(e.target.value))}
        >
          {opts.map((o) => (
            <option key={o} value={o}>
              {fmt.quarter(o)}
              {o === ctx.defaultQuarter ? ` · ${t('quarter.toFile')}` : ''}
            </option>
          ))}
        </select>
      </div>
      <span className="quarter-select__range">
        {fmt.date(range.start)} – {fmt.date(range.end)}
      </span>
    </div>
  );
}
